// Shared cover/entity image upload for the staff forms. Presentational +
// controlled: the orchestrator owns the File handle + the resolved preview URL.
// The 10MB client-side guard matches MAX_IMAGE_BYTES on the backend; the
// server compresses stored images to ~1MB for visitor loading (2026-08-05).
// One copy for zone / exhibit / exhibition — `label` is the only per-owner
// difference (review #8; replaced the byte-identical zones/ + exhibits/ pair).

import type { FC, ChangeEvent } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export interface ImageUploadProps {
  label: string;
  previewUrl?: string | null;
  onFileSelected: (file: File) => void;
  onFileTooLarge?: () => void;
  /** Staged removal (2026-09-08): stage "remove the saved cover" — applied by
   *  the form's Save. Omit when there is no saved cover to remove. */
  onRemove?: () => void;
  /** A removal is staged: hide the preview, show the notice + Undo. */
  removalPending?: boolean;
  onUndoRemove?: () => void;
}

const ImageUpload: FC<ImageUploadProps> = ({
  label,
  previewUrl,
  onFileSelected,
  onFileTooLarge,
  onRemove,
  removalPending = false,
  onUndoRemove,
}) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      onFileTooLarge?.();
      return;
    }
    onFileSelected(file);
  };

  return (
    <div className="space-y-4" data-testid="image-upload-section">
      <Label>{label}</Label>
      <div className="flex flex-col gap-4">
        {removalPending ? (
          <div
            className="w-full max-w-md rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-center justify-between gap-3"
            data-testid="cover-removal-pending"
          >
            <span>
              The cover image will be removed when you save. Visitors will see a
              placeholder until a new one is uploaded.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onUndoRemove}
              data-testid="undo-remove-cover-button"
            >
              Undo
            </Button>
          </div>
        ) : (
          previewUrl && (
            <div className="w-full max-w-md space-y-2">
              <img
                src={previewUrl}
                alt="Preview"
                className="w-full h-64 object-cover rounded-lg border border-border"
                data-testid="image-preview"
              />
              {onRemove && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onRemove}
                  data-testid="remove-cover-button"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove cover image
                </Button>
              )}
            </div>
          )
        )}
        <div>
          <Input
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleChange}
            data-testid="image-upload-input"
          />
          <p className="text-xs text-muted-foreground mt-1">
            JPG or PNG, up to 10MB — large images are compressed automatically
            for fast visitor loading
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
